import React from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";

import { Button } from "components/hds";
import { Spinner } from "components/ui";
import LiveViewModal from "components/LiveView/LiveViewModal";
import { useLiveViewStore } from "components/LiveView/useLiveViewStore";
import { useScenes } from "contexts/Scenes";
import useIsB2cContext from "hooks/useIsB2cContext";
import { StreamingState } from "views/SharedScene/StreamingStatesConstants";

/**
 * Live View: saves the scene, then shows it in the Navigator in a near
 * full-window dialog. For the b2c editor only, where the Navigator has no tab
 * (see Scene.tsx) — and for every b2c user, whatever the organization's plan
 * says about the Navigator: a personal space has no plan at all.
 *
 * The save comes first because the Navigator loads the scene from the server,
 * and a scene that has just arrived from the anonymous configurator has no
 * architecture GLBs until its first save in the editor. Only once the backend
 * has confirmed that save (or there was nothing to save) does the session
 * start — behind this spinning button — and the dialog opens when the
 * Navigator begins loading the scene (useLiveViewStore, LiveViewModal).
 */
const LiveViewButton = () => {
	const { t } = useTranslation();
	const isB2cContext = useIsB2cContext();
	const { current: currentScene, streamingState, streamingCloseEvent } = useScenes();
	const status = useLiveViewStore((state) => state.status);
	const minimized = useLiveViewStore((state) => state.minimized);
	// Registered by SceneView: without an open editor there is nothing to save
	// and so nothing to open.
	const editorReady = useLiveViewStore((state) => state.saveForLiveView !== null);
	const requestLiveView = useLiveViewStore((state) => state.requestLiveView);

	// The header grid places children by position, so an empty span has to
	// hold the column open rather than rendering nothing (as ShareButton).
	if (!isB2cContext || !currentScene || !editorReady) {
		return <span>{/* Do not delete this span */}</span>;
	}

	// Minimized, and the session still up: not ended by the timer or the close
	// button (back to welcome), nor by the streaming side (AFK, error).
	const sessionAlive = streamingState !== StreamingState.WELCOME
		&& streamingState !== StreamingState.CLOSED_ON_AFK_ERROR
		&& !streamingCloseEvent;
	const showLiveDot = minimized && status === "idle" && sessionAlive;

	const saving = status === "saving";
	// The session is starting behind the scenes; the dialog opens on its own
	// once the Navigator loads the scene (LiveViewModal).
	const busy = saving || status === "starting";

	return (
		<>
			<Button
				className="liveViewButton"
				variant="secondary"
				inline
				disabled={busy}
				aria-busy={busy}
				icon={busy
					? <Spinner maxSize="16px" />
					: showLiveDot ? <LiveDot aria-hidden="true" /> : undefined}
				onClick={() => requestLiveView()}
			>
				{String(t(saving ? "app.liveView.saving" : "app.liveView.button"))}
			</Button>
			<LiveViewModal />
		</>
	);
};

const pulse = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0.25; }
`;

/* "Still running": the minimized session's indicator. White, like everything
   else on the button's purple fill. Steady instead of blinking for users who
   asked for less motion — scoped to this element, never a global reset (see
   App.tsx on what that did to antd popups). */
const LiveDot = styled.span`
	display: block;
	width: 10px;
	height: 10px;
	border-radius: var(--radius-full);
	background: var(--color-secondary-contrast);
	animation: ${pulse} 1.2s ease-in-out infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

export default LiveViewButton;
